
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CloudRain, Thermometer, Wind, Eye, TrendingDown, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

export const EnvironmentalIntelligence: React.FC = () => {
  const { user } = useAuth();
  const [analysisWeeks, setAnalysisWeeks] = useState(12);

  // Fetch environmental performance data from running_performance table
  const { data: environmentalData } = useQuery({
    queryKey: ['environmental-performance', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('running_performance')
        .select('recorded_date, pace_per_km_seconds, temperature_celsius, humidity_percentage, weather_data')
        .eq('user_id', user.id)
        .not('weather_data', 'is', null)
        .order('recorded_date', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Fetch environmental impact analysis
  const { data: impactAnalysis } = useQuery({
    queryKey: ['environmental-impact', user?.id, analysisWeeks],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase.rpc('analyze_environmental_impact', {
        p_user_id: user.id,
        p_weeks_back: analysisWeeks
      });
      
      if (error) throw error;
      return data[0];
    },
    enabled: !!user,
  });

  // Process data for charts - using actual running performance data
  const temperatureData = environmentalData?.filter(d => d.temperature_celsius !== null).map(d => ({
    temperature: d.temperature_celsius || 15,
    pace: d.pace_per_km_seconds || 300,
    date: d.recorded_date,
    humidity: d.humidity_percentage || 50
  })) || [];

  const humidityData = environmentalData?.filter(d => d.humidity_percentage !== null).map(d => ({
    humidity: d.humidity_percentage || 50,
    pace: d.pace_per_km_seconds || 300,
    temperature: d.temperature_celsius || 20
  })) || [];

  // Calculate optimal conditions
  const optimalTemp = temperatureData.length > 0 ? 
    temperatureData.reduce((acc, curr) => acc + curr.temperature, 0) / temperatureData.length : 15;
  
  const optimalHumidity = humidityData.length > 0 ?
    humidityData.reduce((acc, curr) => acc + curr.humidity, 0) / humidityData.length : 50;

  return (
    <div className="space-y-6">
      {/* Environmental Impact Summary */}
      {impactAnalysis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Thermometer className="h-4 w-4 text-red-600" />
                Temperature Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">
                  {impactAnalysis.temperature_impact > 0 ? '+' : ''}{impactAnalysis.temperature_impact.toFixed(1)}%
                </span>
                {impactAnalysis.temperature_impact > 0 ? (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                ) : (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Performance change in heat
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CloudRain className="h-4 w-4 text-blue-600" />
                Humidity Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">
                  {impactAnalysis.humidity_impact > 0 ? '+' : ''}{impactAnalysis.humidity_impact.toFixed(1)}%
                </span>
                {impactAnalysis.humidity_impact > 0 ? (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                ) : (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Performance change in humidity
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Eye className="h-4 w-4 text-green-600" />
                Optimal Range
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold">{impactAnalysis.optimal_temp_range}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Best performance conditions
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Wind className="h-4 w-4 text-purple-600" />
                Data Quality
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold">{environmentalData?.length || 0}</div>
              <Badge variant="secondary" className={`mt-1 ${
                (environmentalData?.length || 0) > 20 ? 'bg-green-100 text-green-800' :
                (environmentalData?.length || 0) > 10 ? 'bg-orange-100 text-orange-800' :
                'bg-red-100 text-red-800'
              }`}>
                {(environmentalData?.length || 0) > 20 ? 'High' :
                 (environmentalData?.length || 0) > 10 ? 'Medium' : 'Low'} confidence
              </Badge>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Temperature vs Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Thermometer className="h-5 w-5" />
            Temperature vs Performance
          </CardTitle>
          <CardDescription>
            How temperature affects your running pace (lower is better)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart data={temperatureData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="temperature" 
                  name="Temperature (°C)"
                  domain={['dataMin - 2', 'dataMax + 2']}
                />
                <YAxis 
                  type="number" 
                  dataKey="pace" 
                  name="Pace (seconds/km)"
                  domain={['dataMin - 10', 'dataMax + 10']}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    name === 'pace' ? `${Math.floor(value / 60)}:${(value % 60).toFixed(0).padStart(2, '0')}/km` : `${value}°C`,
                    name === 'pace' ? 'Pace' : 'Temperature'
                  ]}
                  cursor={{ strokeDasharray: '3 3' }}
                />
                <Scatter 
                  dataKey="pace" 
                  fill="#8884d8" 
                  fillOpacity={0.6}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Humidity vs Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CloudRain className="h-5 w-5" />
            Humidity vs Performance
          </CardTitle>
          <CardDescription>
            How humidity levels impact your running performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart data={humidityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="humidity" 
                  name="Humidity (%)"
                  domain={[0, 100]}
                />
                <YAxis 
                  type="number" 
                  dataKey="pace" 
                  name="Pace (seconds/km)"
                  domain={['dataMin - 10', 'dataMax + 10']}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    name === 'pace' ? `${Math.floor(value / 60)}:${(value % 60).toFixed(0).padStart(2, '0')}/km` : `${value}%`,
                    name === 'pace' ? 'Pace' : 'Humidity'
                  ]}
                />
                <Scatter 
                  dataKey="pace" 
                  fill="#82ca9d" 
                  fillOpacity={0.6}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Environmental Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Environmental Training Recommendations</CardTitle>
          <CardDescription>
            Optimize your training based on environmental conditions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {impactAnalysis && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Key Insights</h4>
                <p className="text-sm text-blue-700">{impactAnalysis.performance_recommendations}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">Optimal Conditions</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Temperature: {Math.round(optimalTemp)}°C ± 3°C</li>
                  <li>• Humidity: {Math.round(optimalHumidity)}% ± 10%</li>
                  <li>• Early morning or evening runs</li>
                  <li>• Avoid midday heat in summer</li>
                </ul>
              </div>

              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-medium text-orange-800 mb-2">Hot Weather Strategy</h4>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Start 10-15% slower than usual</li>
                  <li>• Increase hydration before and during</li>
                  <li>• Take walk breaks when needed</li>
                  <li>• Consider indoor training alternatives</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
